import { useMemo, useState } from 'react';
import {
  useTranslate,
  useNotification,
  useInvalidate,
  useCustomMutation,
  useList,
} from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Input, Form, Row, Col, Tag, Button } from 'antd';
import { SearchOutlined, SyncOutlined } from '@ant-design/icons';
import { extractBackendMessage } from '@/utils/apiError';
import type {
  MonetizationProductCatalogSyncOut,
  MonetizationProductOut,
  TranslatedStr,
} from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useAdminTable } from '@/hooks/useAdminTable';
import { SelectFilter } from '@/components/common/SelectFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { RevenueCatProductLink } from '@/components/monetization/RevenueCatLink';
import { ShowLink } from '@/components/common/ShowLink';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const MonetizationProductList = () => {
  const t = useTranslate();
  const { onSearch, createDebouncedSubmit } = useTableSearch();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const { mutateAsync } = useCustomMutation();
  const [syncing, setSyncing] = useState(false);

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<MonetizationProductOut>({
      pagination: { pageSize: 20 },
      resource: 'monetization.products',
      onSearch,
      sorters: {
        initial: [{ field: 'id', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  const { result: facetResult } = useList<MonetizationProductOut>({
    resource: 'monetization.products',
    pagination: { mode: 'server', currentPage: 1, pageSize: 1000 },
    queryOptions: { staleTime: 5 * 60 * 1000 },
  });

  const storeOptions = useMemo(() => {
    const values = new Set<string>();
    facetResult?.data.forEach((p: MonetizationProductOut) => {
      if (p.store) values.add(p.store);
    });
    return Array.from(values)
      .sort()
      .map((value) => ({ label: value, value }));
  }, [facetResult]);

  const productTypeOptions = useMemo(() => {
    const values = new Set<string>();
    facetResult?.data.forEach((p: MonetizationProductOut) => {
      if (p.product_type) values.add(p.product_type);
    });
    return Array.from(values)
      .sort()
      .map((value) => ({ label: value, value }));
  }, [facetResult]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await mutateAsync({
        url: 'monetization/products/sync',
        method: 'post',
        values: {},
        successNotification: false,
        errorNotification: false,
      });
      const summary = result?.data as
        | MonetizationProductCatalogSyncOut
        | undefined;
      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: summary
          ? t('monetization.products.sync.success_summary', {
              product_count: summary.product_count,
              new_products: summary.new_products,
              hydrated_count: summary.hydrated_count,
            })
          : t('monetization.products.sync.success'),
      });
      invalidate({
        resource: 'monetization.products',
        invalidates: ['list'],
      });
    } catch (error) {
      const description =
        extractBackendMessage(error) ??
        t('monetization.products.sync.error_fallback');
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description,
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <List
      headerButtons={() => (
        <Button
          type="primary"
          icon={<SyncOutlined spin={syncing} />}
          onClick={handleSync}
          loading={syncing}
        >
          {t('monetization.products.sync.trigger')}
        </Button>
      )}
    >
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('monetization.products.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowLink resource="monetization.products" id={value} />
          )}
        />

        <Table.Column
          dataIndex="revenuecat_product_id"
          title={t('monetization.products.fields.revenuecat_product_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'revenuecat_product_id')}
          render={(value: string) => (
            <RevenueCatProductLink
              id={value}
              title={t('monetization.products.actions.open_in_revenuecat')}
              stopRowClick
            />
          )}
        />

        <Table.Column
          dataIndex="display_name"
          title={t('monetization.products.fields.display_name')}
          render={(value: string | null) => value ?? '-'}
        />

        <Table.Column
          dataIndex="localized_display_name"
          title={t('monetization.products.fields.localized_display_name')}
          render={(value: TranslatedStr | null) =>
            value?.en || value?.pl
              ? `${value.en ?? '-'} / ${value.pl ?? '-'}`
              : '-'
          }
        />

        <Table.Column
          dataIndex="store_product_identifier"
          title={t('monetization.products.fields.store_product_identifier')}
          render={(value: string | null) => value ?? '-'}
        />

        <Table.Column
          dataIndex="store"
          title={t('monetization.products.fields.store')}
          sorter
          sortOrder={getSortOrder(sorters, 'store')}
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'store')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="store"
              minWidth={200}
              placeholder={t('monetization.products.filter.store')}
              options={storeOptions}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="product_type"
          title={t('monetization.products.fields.product_type')}
          sorter
          sortOrder={getSortOrder(sorters, 'product_type')}
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'product_type')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="product_type"
              minWidth={200}
              placeholder={t('monetization.products.filter.product_type')}
              options={productTypeOptions}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('monetization.products.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          dataIndex="updated_at"
          title={t('monetization.products.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'updated_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="updated_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />
      </Table>
    </List>
  );
};
